/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable no-param-reassign */
import * as Cesium from "cesium";
import {
  FileInformationSettingsUpdateDto,
  ReturnNodeDto,
} from "~/api/GeneratedApi";
import { AttenuationSettings } from "../components/adjustment/AttenuationAdjustment";
import { PointColoringType } from "../components/adjustment/PointColorAdjustment";
import { PointSizeSettings } from "../components/adjustment/PointSizeAdjustment";
import { Custom3DTileset } from "../types/CesiumMapItems";
import {
  CesiumPointSettings,
  ColorSettings,
  SavedElevationSettings,
  SavedIntensitySettings,
} from "./colorAdjustments";
import { updateTilesetStyle } from "./updateTilesetStyle";

type EPTSchema = {
  count: number;
  maximum: number;
  mean: number;
  minimum: number;
  name: string;
  offset: number;
  scale: number;
  size: number;
  stddev: number;
  type: string;
  variance: number;
};

export async function setTilesetSettings(
  file: ReturnNodeDto,
  tileset: Custom3DTileset,
): Promise<void> {
  await tileset.readyPromise;
  // Initialize style
  if (!tileset.style) tileset.style = new Cesium.Cesium3DTileStyle();

  tileset.maximumMemoryUsage = 256;

  // Check the metadata and set properties based on that
  const { asset } = tileset;
  if (asset?.ept?.schema?.length) {
    const props = asset.ept.schema as EPTSchema[];
    const z = props.find((p) => p.name === "Z");
    tileset.minZ = z?.minimum ?? 0;
    tileset.maxZ = z?.maximum ?? 0;

    const intensity = props.find((p) => p.name === "Intensity");
    if (intensity?.maximum || intensity?.minimum) {
      tileset.hasIntensity = true;
      tileset.minIntensity = intensity?.minimum ?? 0; // Default 0
      tileset.maxIntensity = intensity?.maximum ?? 255; // Default 255
    } else {
      tileset.hasIntensity = false;
    }

    const red = props.find((p) => p.name === "Red");
    const green = props.find((p) => p.name === "Green");
    const blue = props.find((p) => p.name === "Blue");
    if (
      red?.minimum ||
      red?.maximum ||
      green?.minimum ||
      green?.maximum ||
      blue?.minimum ||
      blue?.maximum
    ) {
      tileset.hasRGB = true;
    } else {
      tileset.hasRGB = false;
    }
  }
  tileset.colorType = tileset.hasRGB ? "RGB" : "default";

  if (!file?.information) return;

  if (file.information.attenuationSettings) {
    const attenuationSettings = JSON.parse(
      file.information.attenuationSettings,
    ) as AttenuationSettings;

    tileset.attenuationSettings = attenuationSettings;

    tileset.maximumScreenSpaceError =
      attenuationSettings.maximumScreenSpaceError;
    tileset.pointCloudShading.attenuation = attenuationSettings.attenuation;

    tileset.pointCloudShading.geometricErrorScale =
      attenuationSettings.geometricErrorScale;

    tileset.pointCloudShading.maximumAttenuation =
      attenuationSettings.maximumAttenuation;

    tileset.pointCloudShading.baseResolution =
      attenuationSettings.baseResolution;

    tileset.pointCloudShading.eyeDomeLighting =
      attenuationSettings.eyeDomeLighting;

    tileset.pointCloudShading.eyeDomeLightingStrength =
      attenuationSettings.eyeDomeLightingStrength;

    tileset.pointCloudShading.eyeDomeLightingRadius =
      attenuationSettings.eyeDomeLightingRadius;
  } else {
    tileset.maximumScreenSpaceError = 32;
    tileset.pointCloudShading.attenuation = true;
  }

  if (file.information.pointSizeSettings) {
    const pointSizeSetttings = JSON.parse(
      file.information.pointSizeSettings,
    ) as PointSizeSettings;
    tileset.pointSizeSettings = pointSizeSetttings;
  } else {
    tileset.pointSizeSettings = { pointSize: 2 };
  }

  if (file.information.elevationSettings) {
    const parsed = JSON.parse(
      file.information.elevationSettings,
    ) as SavedElevationSettings;
    parsed?.colorsAndSteps?.forEach((s) => {
      if (s.isLowest) s.stepMin = Number.NEGATIVE_INFINITY;
      if (s.isHighest) s.stepMax = Number.POSITIVE_INFINITY;
    });
    tileset.elevationSettings = parsed;
  }

  if (file.information.intensitySettings) {
    const parsed = JSON.parse(
      file.information.intensitySettings,
    ) as SavedIntensitySettings;
    parsed?.colorsAndSteps?.forEach((s) => {
      if (s.isLowest) s.stepMin = Number.NEGATIVE_INFINITY;
      if (s.isHighest) s.stepMax = Number.POSITIVE_INFINITY;
    });
    tileset.intensitySettings = parsed;
  }

  if (file.information.colorSettings) {
    tileset.colorSettings = JSON.parse(
      file.information.colorSettings,
    ) as CesiumPointSettings;
  }

  tileset.staticColor = file.information?.staticColor
    ? (JSON.parse(file.information.staticColor) as string)
    : "#F00";

  // Update coloring settings
  tileset.colorType = file.information.coloringType
    ? (JSON.parse(file.information.coloringType) as PointColoringType)
    : "default";
  if (!tileset.colorType || tileset.colorType === "default")
    tileset.colorType = tileset.hasRGB ? "RGB" : "default";

  let val: string | undefined | ColorSettings;
  const useColorSettings =
    tileset.colorSettings &&
    tileset.colorSettings.color.conditions.length > 0 &&
    Object.keys(tileset.colorSettings.defines).length > 0;
  switch (tileset.colorType) {
    case "static":
      val = tileset.staticColor
        ? `color('${tileset.staticColor?.toString()}')`
        : undefined;
      break;

    case "elevation": {
      if (!useColorSettings) break;
      val = tileset.colorSettings.color;
      await updateTilesetStyle({
        tileset,
        type: "defines",
        newValue: tileset.colorSettings.defines,
      });
      break;
    }
    case "RGB":
      val = `\${COLOR}`;
      break;

    case "intensity": {
      await updateTilesetStyle({
        tileset,
        type: "defines",
        newValue: tileset.colorSettings.defines,
      });
      val = tileset.colorSettings.color;

      break;
    }

    case "default":
      val = undefined;
      break;

    default:
      val = undefined;
      break;
  }
  await updateTilesetStyle({
    tileset,
    type: "color",
    newValue: val,
  });
  await updateTilesetStyle({
    tileset,
    type: "pointSize",
    newValue: tileset.pointSizeSettings.pointSize,
  });
}

export function updateTilesetSettings(
  tileset: Custom3DTileset,
  settings: FileInformationSettingsUpdateDto,
): void {
  if (settings.coloringType)
    tileset.colorType = JSON.parse(settings.coloringType) as PointColoringType;

  if (settings.staticColor)
    tileset.staticColor = JSON.parse(settings.staticColor);

  if (settings.pointSizeSettings)
    tileset.pointSizeSettings = JSON.parse(
      settings.pointSizeSettings,
    ) as PointSizeSettings;

  if (settings.attenuationSettings)
    tileset.attenuationSettings = JSON.parse(
      settings.attenuationSettings,
    ) as AttenuationSettings;

  if (settings.colorSettings)
    tileset.colorSettings = JSON.parse(
      settings.colorSettings,
    ) as CesiumPointSettings;

  if (settings.elevationSettings) {
    const parsed = JSON.parse(
      settings.elevationSettings,
    ) as SavedElevationSettings;
    parsed?.colorsAndSteps?.forEach((s) => {
      if (s.isLowest) s.stepMin = Number.NEGATIVE_INFINITY;
      if (s.isHighest) s.stepMax = Number.POSITIVE_INFINITY;
    });
    tileset.elevationSettings = parsed;
  }

  if (settings.intensitySettings) {
    const parsed = JSON.parse(
      settings.intensitySettings,
    ) as SavedIntensitySettings;
    parsed?.colorsAndSteps?.forEach((s) => {
      if (s.isLowest) s.stepMin = Number.NEGATIVE_INFINITY;
      if (s.isHighest) s.stepMax = Number.POSITIVE_INFINITY;
    });
    tileset.intensitySettings = parsed;
  }
}
